import { useState, useEffect } from "react";
import { Box, TextField, Button, Alert } from "@mui/material";
import ActionsService from "../../services/actions.service";

// const reasonOptions = [
//   { value: "pricing_question", label: "Pricing inquiry" },
//   { value: "complex_question", label: "Complex question" },
//   { value: "recommendation_needed", label: "Needs recommendation" },
//   { value: "custom_request", label: "Custom request" },
//   { value: "product_inquiry", label: "Product inquiry" },
//   { value: "support_needed", label: "Support needed" },
//   { value: "other", label: "Other" },
// ];

const EmailLeadForm = ({
  userId,
  agentId,
  defaultEmail = "",
  defaultName = "",
  userSession,
  onSuccess,
}) => {
  const [form, setForm] = useState({
    leadEmail: defaultEmail,
    leadName: defaultName,
    question: "",
    context: "",
    reason: "other",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      leadEmail: prev.leadEmail || defaultEmail,
      leadName: prev.leadName || defaultName,
    }));
  }, [defaultEmail, defaultName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (error) setError(""); // clear error as user types
  };

  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only require email and name for anonymous users
    if (!form.leadEmail || !form.leadName) {
      setError("Please enter your name and email address.");
      return;
    }

    if (!validateEmail(form.leadEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const payload = {
        userId,
        agentId,
        leadEmail: form.leadEmail,
        leadName: form.leadName,
        question: form.question || "Customer requested follow-up",
        context:
          form.context ||
          form.question ||
          "Customer submitted contact information for follow-up",
        reason: form.reason || "other",
        status: "new",
        user: userSession?.user?.userId,
      };

      await ActionsService.createEmailLead(payload);

      setSuccess(true);
      setForm({
        leadEmail: defaultEmail,
        leadName: defaultName,
        question: "",
        context: "",
        reason: "other",
      });

      // Close dialog after 2 seconds on success
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 2000);
    } catch (err) {
      console.error("Failed to submit lead:", err);
      setError("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isSubmitDisabled = loading || !form.leadEmail || !form.leadName;

  if (success) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: 3,
          textAlign: "center",
        }}
      >
        <Alert severity="success">
          Thank you! We've received your information and will follow up with you
          soon.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}
    >
      {error && <Alert severity="error">{error}</Alert>}

      <TextField
        autoFocus
        label="Name"
        name="leadName"
        value={form.leadName}
        onChange={handleChange}
        placeholder="Enter your name"
        required
        fullWidth
      />

      <TextField
        label="Email"
        name="leadEmail"
        type="email"
        value={form.leadEmail}
        onChange={handleChange}
        placeholder="Enter your email address"
        required
        fullWidth
      />

      <TextField
        label="Message (Optional)"
        name="question"
        value={form.question}
        onChange={handleChange}
        placeholder="Any questions or details you'd like to share?"
        multiline
        rows={3}
        fullWidth
      />

      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={isSubmitDisabled}
        fullWidth
        sx={{ mt: 1 }}
      >
        {loading ? "Submitting..." : "Submit"}
      </Button>
    </Box>
  );
};

export default EmailLeadForm;
